import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Loader2, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const MAXCO = 20;
const li = (u) => (u || "").toLowerCase().replace(/^https?:\/\/(www\.)?/, "").replace(/\/+$/, "");
const split = (n) => { const [f, ...r] = (n || "").trim().split(/\s+/); return { first_name: f || "", last_name: r.join(" ") }; };
const OK = (x) => x.email && !["invalid", "bounced"].includes(x.email_status);

const fromRadar = async (coIds) => {
  const lists = await Promise.all(coIds.slice(0, MAXCO).map((id) => api.get(`/admin/rcc/radar/people?company_id=${id}&limit=50`).then((r) => r.data?.items || []).catch(() => [])));
  return lists.flat().map((p) => ({ person_id: p.id, ...split(p.full_name || p.name), ...(p.first_name ? { first_name: p.first_name, last_name: p.last_name || "" } : {}), title: p.title, email: p.email, email_status: p.email_status, linkedin_url: p.linkedin_url, company_id: p.company_id, company_name: p.company_name || p.company?.name }));
};

export const addContacts = async (people, audience) => {
  const prospects = people.map((x) => ({ first_name: x.first_name, last_name: x.last_name, email: x.email, title: x.title, company: x.company_name, linkedin_url: x.linkedin_url, radar_company_id: x.company_id, radar_person_id: x.person_id, source: "radar" }));
  try {
    const { data } = await api.post("/admin/rcc/li/prospects/bulk", { prospects, audience, goal: "hiring_help", offers: ["resume_scan", "elite"], source: "radar" });
    const c = data?.created ?? prospects.length;
    toast.success(c === 1 && people.length === 1 ? `${people[0].first_name} ${people[0].last_name || ""}`.trim() + " added as a draft prospect. Review the sequence before sending." : `${c} decision maker${c === 1 ? "" : "s"} added as draft prospects${data?.skipped ? `, ${data.skipped} skipped` : ""}. Review their sequences before sending.`);
    return true;
  } catch (e) { toast.error(e.response?.status === 404 ? "Bulk import isn't live on the backend yet (spec §57)." : errMsg(e)); return false; }
};

// Radar decision makers at this audience's prospect companies who aren't prospects yet.
export const useRadarContacts = (items, audience) => {
  const coIds = audience === "job_seeker" ? [] : [...new Set(items.map((p) => p.radar_company_id).filter(Boolean))];
  const key = coIds.sort().join(",");
  const known = items.map((p) => `${(p.email || "").toLowerCase()}|${li(p.linkedin_url)}`).sort().join(",");
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!key) return setList(null);
    let live = true;
    api.get(`/admin/rcc/li/radar-contacts?audience=${audience}`).then((r) => r.data?.items || [])
      .catch((e) => (e.response?.status === 404 ? fromRadar(key.split(",")) : []))
      .then((all) => {
        if (!live) return;
        const emails = new Set(items.map((p) => (p.email || "").toLowerCase()).filter(Boolean));
        const lis = new Set(items.map((p) => li(p.linkedin_url)).filter(Boolean));
        setList(all.filter((x) => !(x.email && emails.has(x.email.toLowerCase())) && !(x.linkedin_url && lis.has(li(x.linkedin_url)))));
      });
    return () => { live = false; };
  }, [key, audience, known]); // eslint-disable-line react-hooks/exhaustive-deps
  return list;
};

export const contactOk = OK;

export const AccountContacts = ({ list, audience, onAdded }) => {
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel((list || []).map((x, i) => (OK(x) ? i : null)).filter((i) => i != null)); }, [list]);
  if (!list?.length) return null;
  const add = async () => {
    setBusy(true);
    if (await addContacts(sel.map((i) => list[i]), audience)) { setSel([]); onAdded(); }
    setBusy(false);
  };
  const tog = (i) => setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  return (
    <div className="rounded-2xl border border-[#8224e3]/40 bg-[#8224e3]/[0.06] p-3" data-testid="li-acct">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 text-left text-sm text-zinc-100" data-testid="li-acct-toggle"><Users className="h-4 w-4 text-[#c79bff]" /><span className="flex-1" data-testid="li-acct-count">Radar found {list.length} more decision maker{list.length === 1 ? "" : "s"} at your prospects' companies</span><ChevronDown className={cn("h-4 w-4 text-zinc-500 transition-transform", open && "rotate-180")} /></button>
      {open && <div className="mt-2 space-y-1">
        {list.map((x, i) => (
          <label key={`${x.person_id || x.email}-${i}`} className={cn("flex flex-wrap items-center gap-2 rounded-lg border border-[#27272A] px-2.5 py-2 text-xs", !OK(x) && "opacity-60")} data-testid={`li-acct-row-${i}`}>
            <input type="checkbox" checked={sel.includes(i)} disabled={!OK(x)} onChange={() => tog(i)} className="h-4 w-4 accent-[#D4AF37]" data-testid={`li-acct-check-${i}`} />
            <span className="min-w-0 flex-1 basis-[calc(100%-2rem)] truncate text-zinc-100 sm:basis-auto">{x.first_name} {x.last_name}<span className="text-zinc-500"> · {[x.title, x.company_name].filter(Boolean).join(" · ")}</span></span>
            <span className={cn("break-all pl-6 font-mono text-[11px] sm:pl-0", OK(x) ? "text-zinc-300" : "text-red-300")} data-testid={`li-acct-email-${i}`}>{x.email || "no email yet"}{x.email_status ? ` · ${x.email_status.replace("_", " ")}` : ""}</span>
          </label>
        ))}
        <Button size="sm" onClick={add} disabled={busy || !sel.length} className="mt-1 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="li-acct-add">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Add {sel.length} as draft prospect{sel.length === 1 ? "" : "s"}</Button>
      </div>}
    </div>
  );
};
